/*
 * The top bar (docs/notifications.md): slim, solid and sticky across the
 * content area on every page. Breadcrumbs on the left (they replace the small
 * colored kickers above page titles); the bell, Help and the profile menu on
 * the right. On a phone it keeps the menu button, the brand, the bell and the
 * avatar, and the breadcrumbs become one "‹ Back to …" link under it.
 *
 * Breadcrumbs come from the address, with names from what's already loaded
 * (sites, servers, domains), so no page has to declare them.
 */
import type { ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { useDomains } from "../api/domains";
import { useMailbox, useMailDomains } from "../api/mail";
import { useLogout, useMe, useServers, useSites } from "../api/queries";
import { supportContact, useBrand } from "../lib/brand";
import { cx } from "../lib/cx";
import { useMediaQuery } from "../lib/hooks";
import { useTheme, type ThemePref } from "../lib/theme";
import { Avatar } from "../ui/Avatar";
import { IconChevronLeft, IconChevronRight, IconHelp, IconKeyboard, IconLogOut, IconMail, IconMenu, IconMonitor, IconMoon, IconSun, IconUser, IconExternal } from "../ui/icons";
import { Menu, MenuContent, MenuItem, MenuLabel, MenuRadioGroup, MenuRadioItem, MenuSeparator, MenuTrigger } from "../ui/Menu";
import { NotificationsBell } from "./Notifications";
import { TaskTray } from "./TaskTray";
import { useOrg, useOrgPath } from "./org";
import { APP_PART_LABEL, isAppPart } from "../pages/sites/node/words";
import { securityPartOf, securityParts } from "../pages/sites/security/partList";
import { commentNumberOf, ENV_SECTIONS, FEEDBACK_PART_LABEL, isFeedbackPart, isSettingsPart, sectionLabel, SETTINGS_PART_LABEL, type EnvSection } from "../pages/sites/env/sections";
import { SITE_TABS } from "../pages/sites/SiteTabs";
import { settingsLabel, settingsPageOf } from "./settingsNav";
import { useMemberAccess } from "../api/access";
import { useGroup } from "../api/groups";
import { useSecurityProfile } from "../api/securityProfiles";
import { Wordmark } from "./Sidebar";
import { useShell } from "./ui";

export type Crumb = { label: string; to?: string };

const SECTION: Record<string, string> = {
  sites: "Sites",
  servers: "Servers",
  mail: "Mail",
  domains: "Domains",
  clients: "Clients",
  activity: "Activity",
  settings: "Settings",
  billing: "Billing",
};
const ENV: Record<string, string> = { dev: "Dev", test: "Test", live: "Live" };
/** Sections that are pages, besides SECTION's; any other first part is an address that doesn't exist. */
const PAGES = new Set([...Object.keys(SECTION), "updates", "accounts", "transfers"]);
const SERVER_TABS = new Set(["roles", "security", "activity", "settings"]);
const words = (s: string) => {
  const t = decodeURIComponent(s).replace(/[-_]/g, " ");
  return t.charAt(0).toUpperCase() + t.slice(1);
};
/** Parts whose names aren't their address capitalized (a mail domain's DNS, its aliases). */
const PART_WORDS: Record<string, string> = { dns: "DNS", aliases: "Aliases & forwarding" };
const partWords = (s: string) => PART_WORDS[s] ?? words(s);

/** "Hosterr › Sites › Hosterr Test › Live › Code", from the address and the names already loaded. */
export function useCrumbs(): Crumb[] {
  const { pathname, search } = useLocation();
  const org = useOrg();
  const path = useOrgPath();
  const parts = pathname.replace(/^\/o\/[^/]+\/?/, "").split("/").filter(Boolean);
  const [section, id, ...rest] = parts;
  const sites = useSites();
  const servers = useServers();
  const mailbox = useMailbox(section === "mail" && id === "mailboxes" ? (rest[0] ?? null) : null);
  const mail = useMailDomains(section === "mail" && !!id);
  const domains = useDomains(section === "domains" && !!id);
  // A page inside a Settings page, by its name once the page has it (the same queries, so nothing more is fetched).
  const inSettings = section === "settings";
  const person = useMemberAccess(inSettings && id === "people" ? (rest[0] ?? null) : null);
  const group = useGroup(inSettings && id === "groups" ? (rest[0] ?? "") : "", inSettings && id === "groups");
  const profile = useSecurityProfile(inSettings && id === "security" && rest[0] === "profiles" ? (rest[1] ?? null) : null);
  const out: Crumb[] = [{ label: org.name, to: path() }];
  if (!section) return [...out, { label: "Overview" }];
  // An address that isn't a page: say so, rather than echo the address back as a crumb.
  if (!PAGES.has(section)) return [...out, { label: "Page not found" }];
  out.push({ label: SECTION[section] ?? words(section), to: path(section) });
  if (!id) {
    // Sites' deleted set, with their final backups (?show=deleted; pages/sites/DeletedSites.tsx).
    if (section === "sites" && new URLSearchParams(search).get("show") === "deleted") out.push({ label: "Recently deleted" });
    return out;
  }

  if (section === "sites") {
    const base = `sites/${id}`;
    out.push({ label: sites.data?.find((s) => s.id === id)?.name ?? "Site", to: path(base) });
    const [tab, env, part, sub] = rest;
    if (tab === "env" && env) {
      // Dev, Test and Live by their names; an extra environment's name as it is ("customers").
      out.push({ label: ENV[env] ?? decodeURIComponent(env), to: path(`${base}/env/${env}`) });
      // The same names as the rail (a static or Node.js site's Files and SSH & SFTP).
      const kind = sites.data?.find((s) => s.id === id)?.kind;
      if (part) out.push({ label: (ENV_SECTIONS as readonly string[]).includes(part) ? sectionLabel(part as EnvSection, kind) : words(part), to: sub ? path(`${base}/env/${env}/${part}`) : undefined });
      // A group's part: the App's Variables, Security's Firewall, Settings' Caching, a comment under Feedback.
      const subLabel =
        part === "app" && isAppPart(sub)
          ? APP_PART_LABEL[sub]
          : part === "security" && sub
            ? securityParts(kind === "wordpress").find((p) => p.id === securityPartOf(sub))?.label
            : part === "settings" && isSettingsPart(sub)
              ? SETTINGS_PART_LABEL[sub]
              : part === "feedback" && sub
                ? isFeedbackPart(sub)
                  ? FEEDBACK_PART_LABEL[sub]
                  : commentNumberOf(sub)
                    ? `Comment #${sub}`
                    : undefined
                : undefined;
      if (subLabel) out.push({ label: subLabel });
    } else if (tab === "environments" && (env === "all" || env === "new")) {
      out.push({ label: env === "all" ? "All environments" : "New environment" });
    } else if (tab && (SITE_TABS as readonly string[]).includes(tab)) {
      out.push({ label: words(tab), to: path(`${base}/${tab}`) });
      // A part inside the tab: a site's Settings part (Access, Repository), or a mail domain and its part.
      if (tab === "settings" && env) out.push({ label: words(env) });
      else if (tab === "email" && env) {
        // A mail domain, or the site's own sending (Sending › Sent mail) and the Mailboxes tab without a domain yet.
        out.push({ label: env === "sending" ? "Sending" : env === "mailboxes" ? "Mailboxes" : decodeURIComponent(env), to: path(`${base}/email/${env}`) });
        if (part) out.push({ label: env === "sending" && part === "sent" ? "Sent mail" : partWords(part) });
      }
    }
  } else if (section === "servers") {
    const base = `servers/${id}`;
    out.push({ label: servers.data?.find((s) => s.id === id)?.name ?? "Server", to: path(base) });
    const [tab, part] = rest;
    if (tab && SERVER_TABS.has(tab)) {
      out.push({ label: words(tab), to: path(`${base}/${tab}`) });
      if (part) out.push({ label: part === "intrusion" ? "Intrusion checks" : words(part) });
    }
  } else if (section === "mail") {
    if (id === "mailboxes") {
      // A mailbox sits under its domain: Mail › example.com › you@example.com.
      const mb = mailbox.data;
      if (mb) out.push({ label: mail.data?.find((d) => d.id === mb.domainId)?.domain ?? mb.address.split("@")[1] ?? "Mail domain", to: path(`mail/${mb.domainId}`) });
      out.push({ label: mb?.address ?? "Mailbox", to: rest[1] && rest[0] ? path(`mail/mailboxes/${rest[0]}`) : undefined });
      if (rest[1]) out.push({ label: partWords(rest[1]) });
    } else out.push({ label: mail.data?.find((d) => d.id === id)?.domain ?? "Mail domain", to: path(`mail/${id}`) });
    if (rest[0] && id !== "mailboxes") out.push({ label: partWords(rest[0]) });
  } else if (section === "domains") {
    out.push({ label: id === "prices" ? "Prices" : (domains.data?.find((d) => d.id === id)?.name ?? "Domain") });
  } else if (section === "settings") {
    // The page's own name ("Sign-in and security"); a person and a group sit under Team, a security profile under
    // Site security, each by its name.
    const at = settingsPageOf([id, ...rest].join("/"));
    if (at) out.push({ label: settingsLabel(at) ?? words(id), to: path(`settings/${at}`) });
    if (id === "people" && rest[0]) out.push({ label: person.data?.name ?? "Person" });
    else if (id === "groups" && rest[0]) out.push({ label: group.data?.name ?? "Group" });
    else if (id === "security" && rest[0] === "profiles") out.push({ label: profile.data?.name ?? "Security profile" });
    else if (!at) out.push({ label: words(id) });
  } else if (section === "transfers") {
    out[out.length - 1] = { label: "Transfer request" }; // one request (pages/transfers): there's no list of them to go back to
  } else {
    out.push({ label: words(id) });
  }
  // The page you're on isn't a link.
  const last = out[out.length - 1]!;
  out[out.length - 1] = { label: last.label };
  return out;
}

function Breadcrumbs({ crumbs }: { crumbs: Crumb[] }) {
  return (
    <nav aria-label="Breadcrumb" className="min-w-0">
      <ol className="flex min-w-0 items-center gap-1 text-[13px]">
        {crumbs.map((c, i) => (
          <li key={i} className={cx("flex min-w-0 items-center gap-1", i < crumbs.length - 2 && "shrink-[2]")}>
            {i ? <IconChevronRight size={13} className="shrink-0 text-faint" /> : null}
            {c.to ? (
              <Link to={c.to} className="truncate rounded-[6px] px-1 py-0.5 font-medium text-dim transition-colors hover:bg-hover hover:text-fg focus-visible:outline-2 focus-visible:outline-brand-500">
                {c.label}
              </Link>
            ) : (
              <span aria-current="page" className="truncate px-1 font-semibold text-fg">
                {c.label}
              </span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

const iconBtn =
  "inline-flex h-9 w-9 items-center justify-center rounded-[9px] text-body transition-colors hover:bg-hover hover:text-fg focus-visible:outline-2 focus-visible:outline-brand-500 data-[state=open]:bg-hover data-[state=open]:text-fg";

/** Getting in touch: the brand's support email and page (lib/brand.ts supportContact), as menu items. */
function SupportItems() {
  const { name, email, url } = supportContact(useBrand());
  return (
    <>
      {email ? (
        <MenuItem icon={<IconMail size={16} />} onSelect={() => window.open(`mailto:${email}`, "_self")}>
          <span className="block">Contact {name}</span>
          <span className="block text-[12px] text-dim">{email}</span>
        </MenuItem>
      ) : null}
      {url ? (
        <MenuItem icon={<IconExternal size={16} />} onSelect={() => window.open(url, "_blank", "noopener")}>
          {name} support
        </MenuItem>
      ) : null}
    </>
  );
}

/** Help: getting in touch (Hosterr on its own panel, the agency on a branded one), and the keyboard shortcuts. */
function HelpMenu() {
  const brand = useBrand();
  const shell = useShell();
  const { email, url } = supportContact(brand);
  return (
    <Menu>
      <MenuTrigger aria-label="Help" className={iconBtn}>
        <IconHelp size={19} />
      </MenuTrigger>
      <MenuContent align="end" className="w-[260px]">
        <MenuLabel>Help</MenuLabel>
        <SupportItems />
        {email || url ? <MenuSeparator /> : null}
        <MenuItem icon={<IconKeyboard size={16} />} shortcut="?" onSelect={() => shell.openShortcuts()}>
          Keyboard shortcuts
        </MenuItem>
      </MenuContent>
    </Menu>
  );
}

const themeIcons: Record<ThemePref, ReactNode> = { light: <IconSun size={16} />, dark: <IconMoon size={16} />, system: <IconMonitor size={16} /> };

/** You: name and email, Account, Theme, Sign out. */
function ProfileMenu() {
  const me = useMe();
  const path = useOrgPath();
  const navigate = useNavigate();
  const logout = useLogout();
  const { pref, setPref } = useTheme();
  // The top bar's Help menu is hidden on a phone: getting in touch is here instead.
  const phone = useMediaQuery("(max-width: 639px)");
  const support = supportContact(useBrand());
  const user = me.data?.user;
  if (!user) return null;
  return (
    <Menu>
      <MenuTrigger aria-label={`Account menu for ${user.name}`} className="inline-flex h-9 w-9 items-center justify-center rounded-full transition-shadow hover:shadow-[0_0_0_3px_var(--hx-hover)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 data-[state=open]:shadow-[0_0_0_3px_var(--hx-line)]">
        <span aria-hidden="true" className="inline-flex">
          <Avatar name={user.name} size={32} />
        </span>
      </MenuTrigger>
      <MenuContent align="end" className="w-[264px]">
        <div className="flex items-center gap-3 px-2.5 pt-2 pb-2.5">
          <Avatar name={user.name} size={36} />
          <span className="min-w-0 leading-tight">
            <span className="block truncate text-[13.5px] font-semibold text-fg">{user.name}</span>
            <span className="block truncate text-[12px] text-dim">{user.email}</span>
          </span>
        </div>
        <MenuSeparator />
        <MenuItem icon={<IconUser size={16} />} onSelect={() => navigate(path("settings/profile"))}>
          <span className="block">Account</span>
          <span className="block text-[12px] text-dim">Profile and sign-in</span>
        </MenuItem>
        <MenuSeparator />
        <MenuLabel>Theme</MenuLabel>
        <MenuRadioGroup value={pref} onValueChange={(v) => setPref(v as ThemePref)}>
          <MenuRadioItem value="light" icon={themeIcons.light}>
            Light
          </MenuRadioItem>
          <MenuRadioItem value="dark" icon={themeIcons.dark}>
            Dark
          </MenuRadioItem>
          <MenuRadioItem value="system" icon={themeIcons.system}>
            Match system
          </MenuRadioItem>
        </MenuRadioGroup>
        <MenuSeparator />
        {phone && (support.email || support.url) ? (
          <>
            <MenuLabel>Help</MenuLabel>
            <SupportItems />
            <MenuSeparator />
          </>
        ) : null}
        <MenuItem icon={<IconLogOut size={16} />} onSelect={() => logout.mutate(undefined, { onSettled: () => navigate("/login", { replace: true }) })}>
          Sign out
        </MenuItem>
      </MenuContent>
    </Menu>
  );
}

export function TopBar({ onOpenNav }: { onOpenNav: () => void }) {
  const crumbs = useCrumbs();
  // On a phone: back to the thing this page belongs to (the site, the server), or to its list from the thing itself.
  const parent = crumbs.length >= 4 ? crumbs[2] : crumbs.length === 3 ? crumbs[1] : null;
  return (
    <header className="glass-header sticky top-0 z-30 border-b border-line">
      <div className="flex h-14 items-center gap-2 px-3 sm:px-5 lg:px-8">
        <button type="button" onClick={onOpenNav} aria-label="Open navigation" className={cx(iconBtn, "lg:hidden")}>
          <IconMenu size={20} />
        </button>
        <span className="lg:hidden">
          <Wordmark />
        </span>
        <div className="hidden min-w-0 flex-1 lg:block">
          <Breadcrumbs crumbs={crumbs} />
        </div>
        <div className="ml-auto flex shrink-0 items-center gap-1">
          <TaskTray />
          <NotificationsBell />
          <span className="hidden sm:inline-flex">
            <HelpMenu />
          </span>
          <span className="ml-1">
            <ProfileMenu />
          </span>
        </div>
      </div>
      {parent?.to ? (
        <div className="flex h-9 items-center border-t border-line-soft px-3 lg:hidden">
          <Link to={parent.to} className="inline-flex min-w-0 items-center gap-1 rounded-[6px] px-1 text-[13px] font-medium text-dim hover:text-fg focus-visible:outline-2 focus-visible:outline-brand-500">
            <IconChevronLeft size={15} className="shrink-0" />
            <span className="truncate">Back to {parent.label}</span>
          </Link>
        </div>
      ) : null}
    </header>
  );
}
